/**
 * Server-side API Helpers
 * Helper functions cho server actions và API routes
 * Sử dụng Kubernetes internal URLs
 */

import { HttpClient } from '../../api/https/client';
import { getServiceUrl, SERVICES, type ServiceKey } from '@/config';
import { getAccessToken } from '../auth/token-manager';
import { refreshSessionToken } from '../auth/token-manager';
import { handleLogout } from '../auth/token-manager';
import type { RequestOptions } from '../../api/types';

/**
 * Tạo HTTP client cho server-side với internal URL
 */
function createServerClient(service: ServiceKey, skipAuth = false): HttpClient {
  const baseUrl = getServiceUrl(service, 'server');

  return new HttpClient({
    baseUrl,
    name: `${service} (Server${skipAuth ? ' - Public' : ''})`,
    defaultHeaders: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'Accept-language': 'vi',
    },
    // Chỉ set auth nếu không skip
    getAuthToken: skipAuth ? undefined : getAccessToken,
    refreshToken: skipAuth ? undefined : refreshSessionToken,
    onUnauthorized: skipAuth ? undefined : handleLogout,
  });
}

type GetOptions = Omit<RequestOptions, 'path' | 'method' | 'query'> & {
  skipAuth?: boolean;
};

/**
 * GET request từ server
 * @param skipAuth - Set true để gọi API public (không cần token)
 *
 * Có thể gọi theo 2 cách:
 * - getFromService(service, path, { skipAuth: true }) // Không có query
 * - getFromService(service, path, { id: 1 }, { skipAuth: true }) // Có query
 */
export async function getFromService<TResponse = unknown>(
  service: ServiceKey,
  path: string,
  queryOrOptions?: RequestOptions['query'] | GetOptions,
  options?: GetOptions
): Promise<TResponse> {
  // Nếu tham số thứ 3 có skipAuth thì đó là options, không phải query
  const isOptionsOnly =
    queryOrOptions &&
    typeof queryOrOptions === 'object' &&
    !Array.isArray(queryOrOptions) &&
    'skipAuth' in queryOrOptions;

  const query = isOptionsOnly
    ? undefined
    : (queryOrOptions as RequestOptions['query']);
  const finalOptions = isOptionsOnly ? (queryOrOptions as GetOptions) : options;

  const client = createServerClient(service, finalOptions?.skipAuth);

  return client.get<TResponse>(path, query, finalOptions);
}

/**
 * POST request từ server
 * @param skipAuth - Set true để gọi API public (không cần token)
 */
export async function postToService<TResponse = unknown, TBody = unknown>(
  service: ServiceKey,
  path: string,
  body?: TBody,
  options?: Omit<RequestOptions<TBody>, 'path' | 'method' | 'body'> & {
    skipAuth?: boolean;
  }
): Promise<TResponse> {
  const client = createServerClient(service, options?.skipAuth);

  return client.post<TResponse, TBody>(path, body, options);
}

/**
 * PUT request từ server
 * @param skipAuth - Set true để gọi API public (không cần token)
 */
export async function putToService<TResponse = unknown, TBody = unknown>(
  service: ServiceKey,
  path: string,
  body?: TBody,
  options?: Omit<RequestOptions<TBody>, 'path' | 'method' | 'body'> & {
    skipAuth?: boolean;
  }
): Promise<TResponse> {
  const client = createServerClient(service, options?.skipAuth);

  return client.put<TResponse, TBody>(path, body, options);
}

/**
 * PATCH request từ server
 * @param skipAuth - Set true để gọi API public (không cần token)
 */
export async function patchToService<TResponse = unknown, TBody = unknown>(
  service: ServiceKey,
  path: string,
  body?: TBody,
  options?: Omit<RequestOptions<TBody>, 'path' | 'method' | 'body'> & {
    skipAuth?: boolean;
  }
): Promise<TResponse> {
  const client = createServerClient(service, options?.skipAuth);

  return client.patch<TResponse, TBody>(path, body, options);
}

/**
 * DELETE request từ server
 * @param skipAuth - Set true để gọi API public (không cần token)
 */
export async function deleteFromService<TResponse = unknown, TBody = unknown>(
  service: ServiceKey,
  path: string,
  body?: TBody,
  options?: Omit<RequestOptions<TBody>, 'path' | 'method' | 'body'> & {
    skipAuth?: boolean;
  }
): Promise<TResponse> {
  const client = createServerClient(service, options?.skipAuth);

  return client.delete<TResponse, TBody>(path, body, options);
}

/**
 * Generic fetch từ service
 * @param skipAuth - Set true để gọi API public (không cần token)
 */
export async function fetchFromService<TResponse = unknown, TBody = unknown>(
  service: ServiceKey,
  options: RequestOptions<TBody> & { skipAuth?: boolean }
): Promise<TResponse> {
  const client = createServerClient(service, options?.skipAuth);

  return client.request<TResponse, TBody>(options);
}

/**
 * Re-export SERVICES constant
 */
export { SERVICES };
